import React, { useState, Fragment, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import API from '../../services/API';

const StudentPage = () => {
    const [students, setStudents] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState("");
    const [expandedRows, setExpandedRows] = useState({});

    // ==================== API Helpers ====================
    const fetchStudents = (page = 1, limit = 10, searchTerm = "") => {
        const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'token': localStorage.getItem('token'),
        };

        let url = `/portal/getStudents?page=${page}&limit=${limit}`;
        if (searchTerm) {
            url += `&search=${encodeURIComponent(searchTerm)}`;
        }

        API.get(url, { headers })
            .then((response) => {
                if (response.data.response_code === 1000) {
                    setStudents(response.data.data.students);
                    setTotalRows(response.data.data.total);
                } else {
                    toast.error(`${response.data.message}`, { autoClose: 5000 });
                }
            })
            .catch(() => toast.error("Error fetching students", { autoClose: 5000 }));
    };

    const fetchStudentSummary = (studentId) => {
        const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'token': localStorage.getItem('token'),
        };

        API.get(`/analytics/attendance/studentSummary?student_id=${studentId}`, { headers })
            .then((response) => {
                if (response.data.response_code === 1000) {
                    setExpandedRows((prev) => ({
                        ...prev,
                        [studentId]: response.data.data,
                    }));
                } else {
                    toast.error(`${response.data.message}`, { autoClose: 5000 });
                }
            })
            .catch(() => toast.error("Error fetching summary", { autoClose: 5000 }));
    };

    const updateTwinStatus = async (student_uuid, is_twin) => {
        try {
            const res = await API.post(
                "/student/updateTwinStatus",
                { student_uuid, is_twin },
                {
                    headers: {
                        "Content-Type": "application/json",
                        token: localStorage.getItem("token"),
                    },
                }
            );
            if (res.data.response_code === 1000) {
                toast.success("Twin status updated");
            } else {
                toast.error(res.data.message || "Failed to update twin status");
            }
        } catch (err) {
            toast.error("Error updating twin status");
        }
    };

    const handleTwinToggle = async (student_uuid, checked) => {
        await updateTwinStatus(student_uuid, checked ? 1 : 0);
        setStudents((prev) =>
            prev.map((s) =>
                s.student_uuid === student_uuid ? { ...s, is_twin: checked ? 1 : 0 } : s
            )
        );
    };

    const handleAccordionToggle = (studentId) => {
        if (expandedRows[studentId]) {
            setExpandedRows((prev) => {
                const newRows = { ...prev };
                delete newRows[studentId];
                return newRows;
            });
        } else {
            fetchStudentSummary(studentId);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchStudents(page, perPage, search);
    };

    const handlePerPageChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        fetchStudents(page, newPerPage, search);
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearch(value);
        setCurrentPage(1);
        fetchStudents(1, perPage, value);
    };

    useEffect(() => {
        fetchStudents(currentPage, perPage, search);
    }, []);

    // ==================== Table Columns ====================
    const columns = [
        {
            name: "First Name",
            selector: (row) => row.first_name,
            sortable: true,
            center: true,
        },
        {
            name: "Class Name",
            selector: (row) => row.class_name || "Unassigned",
            center: true,
        },
        {
            name: "Twin",
            selector: (row) => (
                <div className="form-check form-switch d-flex justify-content-center">
                    <input
                        type="checkbox"
                        className="form-check-input"
                        checked={!!row.is_twin}
                        onChange={(e) => handleTwinToggle(row.student_uuid, e.target.checked)}
                    />
                </div>
            ),
            center: true,
        },
        {
            name: "Status",
            selector: (row) => {
                let label = "N/A";
                let badgeClass = "bg-secondary";
                const status = parseInt(row.status, 10);
                switch (status) {
                    case 0:
                        label = "Unrecorded"; badgeClass = "bg-warning"; break;
                    case 1:
                        label = "Recording"; badgeClass = "bg-info"; break;
                    case 2:
                        label = "Parent Linked"; badgeClass = "bg-success"; break;
                    case 4:
                        label = "Recorded (No Parents)"; badgeClass = "bg-danger"; break;
                    case 10:
                        label = "Inactive"; badgeClass = "bg-dark"; break;
                    default:
                        label = "Unknown"; badgeClass = "bg-secondary";
                }
                return <span className={`badge ${badgeClass}`}>{label}</span>;
            },
            center: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleAccordionToggle(row.student_uuid)}
                >
                    {expandedRows[row.student_uuid] ? "Hide Summary" : "Show Summary"}
                </button>
            ),
            center: true,
        },
    ];

    // ==================== Render ====================
    return (
        <Fragment>
            <div className="container-fluid">
                <div className="row">
                    <div className="col-xl-12 xl-50">
                        <div className="card rounded-25">
                            <div className="card-header rounded-25 text-red d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">Students</h5>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    style={{ maxWidth: "250px" }}
                                    placeholder="Search by name..."
                                    value={search}
                                    onChange={handleSearch}
                                />
                            </div>
                            <div className="card-body">
                                <div className="table-responsive">
                                    <DataTable
                                        columns={columns}
                                        data={students}
                                        striped
                                        persistTableHead
                                        pagination
                                        paginationServer
                                        paginationTotalRows={totalRows}
                                        paginationPerPage={perPage}
                                        paginationRowsPerPageOptions={[10, 20, 50]}
                                        onChangePage={handlePageChange}
                                        onChangeRowsPerPage={handlePerPageChange}
                                        expandableRows
                                        expandableRowsComponent={({ data }) => {
                                            const summary = expandedRows[data.student_uuid];
                                            if (!summary) return <div className="p-2">Loading...</div>;
                                            return (
                                                <div className="p-2">
                                                    <h6 className="mb-2">Attendance Summary ({summary.year})</h6>
                                                    <div className="table-responsive d-inline-block">
                                                        <table className="table table-sm table-bordered align-middle mb-0" style={{ width: "auto" }}>
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th>Month</th>
                                                                    <th className="text-center">Present</th>
                                                                    <th className="text-center">Absent</th>
                                                                    <th className="text-center">Total</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {summary.summary.map((month, idx) => (
                                                                    <React.Fragment key={idx}>
                                                                        <tr>
                                                                            <td><strong>{month.month}</strong></td>
                                                                            <td className="text-success text-center">{month.present_days}</td>
                                                                            <td className="text-danger text-center">{month.absent_days}</td>
                                                                            <td className="text-center">{month.total_days}</td>
                                                                        </tr>
                                                                        {month.details?.length > 0 && (
                                                                            <tr>
                                                                                <td colSpan="4" className="bg-light">
                                                                                    <ul className="list-unstyled mb-0 ps-2">
                                                                                        {month.details.map((detail, i) => (
                                                                                            <li key={i}>
                                                                                                <strong>{detail.date}</strong>: {detail.reason || "No reason"}{" "}
                                                                                                {detail.letter_attachment_url && (
                                                                                                    <a
                                                                                                        href={detail.letter_attachment_url}
                                                                                                        target="_blank"
                                                                                                        rel="noopener noreferrer"
                                                                                                        className="text-primary"
                                                                                                    >
                                                                                                        (View Letter)
                                                                                                    </a>
                                                                                                )}
                                                                                            </li>
                                                                                        ))}
                                                                                    </ul>
                                                                                </td>
                                                                            </tr>
                                                                        )}
                                                                    </React.Fragment>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            );
                                        }}
                                        expandableRowExpanded={(row) => !!expandedRows[row.student_uuid]}
                                        onRowExpandToggled={(expanded, row) => {
                                            if (expanded && !expandedRows[row.student_uuid]) {
                                                fetchStudentSummary(row.student_uuid);
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default StudentPage;
